import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useSport } from '../context/SportContext';
import HeaderStats from '../components/HeaderStats';
import FilterSection from '../components/FilterSection';
import NHLConfidenceTiers from '../components/NHLConfidenceTiers';
import { enrichPredictionsWithResults } from '../utils/api';
const NBADashboard = () => {
    console.log('🏀 NBADashboard component is rendering');
    const { currentSport } = useSport();
    const [predictions, setPredictions] = useState(null);
    const [filteredGames, setFilteredGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        team: '',
        confidence: 'ALL',
        conference: 'ALL',
        week: 'ALL',
        date: 'ALL'
    });
    useEffect(() => {
        const loadPredictions = async () => {
            try {
                setLoading(true);
                const timestamp = Date.now();
                const response = await fetch(`/static/nba/nba_predictions.json?v=${timestamp}`, {
                    headers: {
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                        'Expires': '0'
                    }
                });
                console.log('🏀 Fetching NBA predictions with timestamp:', timestamp);
                if (!response.ok) {
                    throw new Error('Failed to fetch NBA predictions');
                }
                const data = await response.json();
                console.log('🏀 Loaded predictions data:', data);
                console.log('🏀 Data has predictions:', !!data?.predictions);
                console.log('🏀 Predictions count:', data?.predictions?.length);
                // Transform the data structure to match what the components expect
                const rawGames = data.predictions || [];
                console.log('🏀 First raw game:', rawGames[0]);
                const games = rawGames.map((game) => {
                    // Extract betting analysis
                    const bettingAnalysis = game.betting_analysis || {};
                    // Map NBA bet_tier to confidence_level (PASS -> LOW for display)
                    let confidenceLevel = bettingAnalysis.bet_tier || 'LOW';
                    if (confidenceLevel === 'PASS')
                        confidenceLevel = 'LOW';
                    const edge = bettingAnalysis.edge_percentage || 0;
                    // Determine team to cover based on recommendation
                    const recommendation = bettingAnalysis.recommendation;
                    const predictedSpread = game.predicted_spread || 0;
                    let teamToCover = game.home_team;
                    let predictedWinner = 'HOME';
                    let spreadLine = game.market_spread || 0;
                    if (recommendation === 'BET_AWAY') {
                        teamToCover = game.away_team;
                        predictedWinner = 'AWAY';
                        spreadLine = -(game.market_spread || 0); // Flip spread for away team
                    }
                    else if (recommendation === 'BET_HOME') {
                        teamToCover = game.home_team;
                        predictedWinner = 'HOME';
                        spreadLine = game.market_spread || 0;
                    }
                    else {
                        // Fallback to predicted_spread if no recommendation
                        predictedWinner = predictedSpread < 0 ? 'HOME' : 'AWAY';
                        teamToCover = predictedSpread < 0 ? game.home_team : game.away_team;
                        spreadLine = predictedSpread < 0 ? game.market_spread : -(game.market_spread || 0);
                    }
                    // Calculate display confidence (0-100 scale)
                    const displayConfidence = (game.confidence || 0.5) * 100;
                    // Extract betting odds if available
                    const bettingOdds = game.betting_odds || {};
                    return {
                        id: game.game_id || `game_${Math.random()}`,
                        game_id: game.game_id || `game_${Math.random()}`,
                        home_team: game.home_team,
                        away_team: game.away_team,
                        game_date: game.date,
                        game_time: '7:30 PM',
                        venue: game.venue || `${game.home_team} Arena`,
                        week: 1, // NBA doesn't use weeks
                        year: 2025,
                        // Prediction fields
                        confidence_level: confidenceLevel,
                        confidence: game.confidence || 0.5,
                        team_to_cover: teamToCover,
                        predicted_winner: predictedWinner,
                        predicted_spread: predictedSpread,
                        display_confidence: displayConfidence,
                        ensemble_probability: game.confidence || 0.5,
                        // Betting fields
                        spread: game.market_spread,
                        spread_line: spreadLine, // The actual spread for the picked team
                        betting_edge: edge / 100, // Convert percentage to decimal
                        recommendation: bettingAnalysis.recommendation,
                        model_agreement: game.model_agreement,
                        // Betting odds (moneyline, spread, totals)
                        home_ml: bettingOdds.home_moneyline,
                        away_ml: bettingOdds.away_moneyline,
                        home_spread: bettingOdds.home_spread,
                        away_spread: bettingOdds.away_spread,
                        home_spread_odds: bettingOdds.home_spread_odds,
                        away_spread_odds: bettingOdds.away_spread_odds,
                        total: bettingOdds.total_points,
                        over_odds: bettingOdds.over_odds,
                        under_odds: bettingOdds.under_odds,
                        bookmaker: bettingOdds.bookmaker,
                        // Individual model predictions
                        individual_models: game.individual_models,
                        // Store original data for reference
                        raw_data: game,
                        original_prediction_data: game
                    };
                });
                const transformedData = {
                    metadata: data.summary || data.metadata || data,
                    games: games
                };
                console.log('🏀 Transformed games sample:', games[0]);
                console.log('🏀 Games with IDs:', games.map((g) => ({ id: g.id, teams: `${g.away_team} @ ${g.home_team}` })));
                // Enrich with game results
                console.log('🏀 Enriching with game results...');
                const enrichedData = await enrichPredictionsWithResults(transformedData, 'nba');
                console.log('🏀 Enriched data:', enrichedData);
                setPredictions(enrichedData);
                setFilteredGames(enrichedData.games);
            }
            catch (err) {
                setError('Failed to load NBA predictions. Please try again later.');
                console.error('Error loading NBA predictions:', err);
            }
            finally {
                setLoading(false);
            }
        };
        loadPredictions();
    }, [currentSport]);
    useEffect(() => {
        if (!predictions)
            return;
        let filtered = predictions.games;
        // Apply team filter
        if (filters.team) {
            const teamLower = filters.team.toLowerCase();
            filtered = filtered.filter((game) => game.home_team.toLowerCase().includes(teamLower) ||
                game.away_team.toLowerCase().includes(teamLower));
        }
        // Apply confidence filter
        if (filters.confidence !== 'ALL') {
            filtered = filtered.filter((game) => game.confidence_level === filters.confidence);
        }
        console.log('🏀 NBA filtered games:', filtered.length);
        if (filtered.length > 0) {
            console.log('🏀 First game structure:', filtered[0]);
            console.log('🏀 Confidence levels in data:', [...new Set(filtered.map((g) => g.confidence_level))]);
        }
        setFilteredGames(filtered);
    }, [predictions, filters]);
    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
    };
    if (loading) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsx("div", { className: "d-flex justify-content-center", children: _jsx("div", { className: "spinner-border", role: "status", children: _jsx("span", { className: "visually-hidden", children: "Loading..." }) }) }) }));
    }
    if (error) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsxs("div", { className: "alert alert-danger", role: "alert", children: [_jsx("i", { className: "fas fa-exclamation-triangle me-2" }), error] }) }));
    }
    if (!predictions) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsx("div", { className: "alert alert-info", role: "alert", children: "No NBA predictions available at this time." }) }));
    }
    return (_jsxs("div", { className: "container-mobile safe-area-padding", children: [_jsx(HeaderStats, { metadata: predictions?.metadata, games: predictions?.games || [], sport: currentSport }), _jsx(FilterSection, { filters: filters, onFilterChange: handleFilterChange, availableWeeks: [] }), _jsx(NHLConfidenceTiers, { games: filteredGames })] }));
};
export default NBADashboard;
